import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setUser } from '../../store/slices/userSlice';
import { CText, CHeader, CInput, CButton, SEO } from '../../components/common';
import { FiCamera } from 'react-icons/fi';
import routes from '../../utils/routes';
import { updateUserData, fetchUserData } from '../../api/auth';

const PersonalDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [wantsExpert, setWantsExpert] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    profileHeadline: '',
    companyName: '',
    sebiQualification: '',
    sebiRegNumber: '',
    yearsOfExperience: '',
    languages: '',
    isCompanyAssociated: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone_number || '',
        bio: user.bio || '',
        profileHeadline: user.headline || '',
        companyName: user.company_name || '',
        sebiQualification: user.sebi_qualification || '',
        sebiRegNumber: user.sebi_reg_number || '',
        yearsOfExperience: user.years_of_xp?.toString() || '',
        languages: user.languages?.join(', ') || '',
        isCompanyAssociated: user.company_associated || false,
      });
      if (user.image) {
        setImagePreview(user.image);
      }
    }
  }, [user]);

  const isExpert = user?.is_expert || false;
  const showExpertFields = isExpert || wantsExpert;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const authToken = useAppSelector((state) => state.user.authToken);

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!authToken) return;

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('email', formData.email);
      if (formData.phone) payload.append('phone_number', formData.phone);
      if (selectedFile) {
        payload.append('image', selectedFile);
      }
      // If regular user toggled "Become an Expert", set is_expert
      if (!isExpert && wantsExpert) {
        payload.append('is_expert', 'true');
      }
      if (showExpertFields) {
        if (formData.profileHeadline) payload.append('headline', formData.profileHeadline);
        if (formData.bio) payload.append('bio', formData.bio);
        if (formData.sebiQualification) payload.append('sebi_qualification', formData.sebiQualification);
        if (formData.sebiRegNumber) payload.append('sebi_reg_number', formData.sebiRegNumber);
        if (formData.yearsOfExperience) payload.append('years_of_xp', formData.yearsOfExperience);
        const langs = formData.languages.split(',').map((l) => l.trim()).filter(Boolean);
        if (langs.length > 0) payload.append('languages', JSON.stringify(langs));
        payload.append('company_associated', JSON.stringify(formData.isCompanyAssociated));
        if (formData.isCompanyAssociated && formData.companyName) {
          payload.append('company_name', formData.companyName);
        }
      }

      await updateUserData(authToken, payload);

      // Fetch fresh user data from backend to update Redux
      const freshUser = await fetchUserData(authToken);
      dispatch(setUser(freshUser as any));
      navigate(routes.profile);
    } catch (error) {
      console.error('Error saving profile:', error);
      setErrors({ name: 'Failed to save. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const sebiQualifications = [
    'Investment Advisor',
    'Research Analyst',
    'Portfolio Manager',
    'Stockbroker',
    'Merchant Banker',
  ];

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <SEO title="Personal Details" noIndex />
      <div className="lg:max-w-3xl lg:mx-auto">
        <CHeader title="Personal Details" />

        {/* Profile Picture */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold"
                style={{ backgroundColor: colors.primary, color: colors.white }}
              >
                {formData.name.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileSelect}
            />
            <button
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: colors.primary }}
              onClick={() => fileInputRef.current?.click()}
            >
              {React.createElement(FiCamera as any, { size: 16, color: colors.white })}
            </button>
          </div>
        </div>

        {/* Basic Info */}
        <div className="space-y-4">
          <CInput
            label="Full Name"
            value={formData.name}
            onChange={(val) => handleChange('name', val)}
            placeholder="Enter your full name"
            errorText={errors.name}
            required
          />

          <CInput
            label="Email"
            value={formData.email}
            onChange={(val) => handleChange('email', val)}
            placeholder="Enter your email"
            type="email"
            errorText={errors.email}
            required
          />

          <CInput
            label="Phone Number"
            value={formData.phone}
            onChange={(val) => handleChange('phone', val)}
            placeholder="Enter your phone number"
            type="tel"
          />
        </div>

        {/* Become an Expert toggle (for regular users) */}
        {!isExpert && (
          <div className="mt-6">
            <div
              className="flex items-center justify-between p-4 rounded-xl border"
              style={{ borderColor: colors.primary + '40', backgroundColor: colors.primary + '08' }}
            >
              <div>
                <CText type="B14" color={colors.textColor}>
                  Register as Financial Expert
                </CText>
                <CText type="r12" color={colors.grayScale500}>
                  Share your expertise and earn money
                </CText>
              </div>
              <button
                onClick={() => setWantsExpert(!wantsExpert)}
                className="w-12 h-7 rounded-full relative transition-colors"
                style={{ backgroundColor: wantsExpert ? colors.primary : colors.grayScale500 + '40' }}
              >
                <div
                  className="w-5 h-5 rounded-full bg-white absolute top-1 transition-all"
                  style={{ left: wantsExpert ? '26px' : '4px' }}
                />
              </button>
            </div>
          </div>
        )}

        {/* Expert-specific fields */}
        {showExpertFields && (
          <>
            <div className="mt-8 mb-4">
              <CText type="B16" color={colors.textColor}>
                Expert Information
              </CText>
              <CText type="r12" color={colors.grayScale500}>
                This information is visible to users
              </CText>
            </div>

            <div className="space-y-4">
              {/* Company Association */}
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => handleChange('isCompanyAssociated', !formData.isCompanyAssociated)}
                  className="w-5 h-5 rounded border-2 flex items-center justify-center"
                  style={{
                    borderColor: formData.isCompanyAssociated ? colors.primary : colors.grayScale500,
                    backgroundColor: formData.isCompanyAssociated ? colors.primary : 'transparent',
                  }}
                >
                  {formData.isCompanyAssociated && (
                    <span className="text-white text-xs">✓</span>
                  )}
                </button>
                <CText type="r14" color={colors.textColor}>
                  Associated with a company
                </CText>
              </div>

              {formData.isCompanyAssociated && (
                <CInput
                  label="Company Name"
                  value={formData.companyName}
                  onChange={(val) => handleChange('companyName', val)}
                  placeholder="Enter company name"
                />
              )}

              <CInput
                label="Profile Headline"
                value={formData.profileHeadline}
                onChange={(val) => handleChange('profileHeadline', val)}
                placeholder="e.g., Senior Investment Advisor"
              />

              <CInput
                label="Bio / Description"
                value={formData.bio}
                onChange={(val) => handleChange('bio', val)}
                placeholder="Tell users about yourself and your expertise"
                multiline
              />

              {/* SEBI Qualification Dropdown */}
              <div className="mb-3">
                <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 4 }}>
                  SEBI Qualification *
                </CText>
                <select
                  value={formData.sebiQualification}
                  onChange={(e) => handleChange('sebiQualification', e.target.value)}
                  className="w-full lg:max-w-lg p-3 rounded-xl border text-base"
                  style={{
                    backgroundColor: colors.inputBackground,
                    borderColor: colors.borderColor,
                    color: colors.textColor,
                  }}
                >
                  <option value="">Select qualification</option>
                  {sebiQualifications.map((qual) => (
                    <option key={qual} value={qual}>
                      {qual}
                    </option>
                  ))}
                </select>
              </div>

              <CInput
                label="SEBI Registration Number"
                value={formData.sebiRegNumber}
                onChange={(val) => handleChange('sebiRegNumber', val)}
                placeholder="INH000XXXXXX"
              />

              <CInput
                label="Years of Experience"
                value={formData.yearsOfExperience}
                onChange={(val) => handleChange('yearsOfExperience', val)}
                placeholder="e.g., 10"
                type="number"
              />

              <CInput
                label="Languages (comma separated)"
                value={formData.languages}
                onChange={(val) => handleChange('languages', val)}
                placeholder="e.g., English, Hindi, Gujarati"
              />
            </div>

            {/* Expert Actions */}
            <div className="flex gap-3 mt-6">
              <button
                className="flex-1 py-3 rounded-xl border"
                style={{ borderColor: colors.primary }}
                onClick={() => navigate(routes.expertAvailability)}
              >
                <CText type="M14" color={colors.primary}>
                  Set Availability
                </CText>
              </button>
              <button
                className="flex-1 py-3 rounded-xl border"
                style={{ borderColor: colors.primary }}
                onClick={() => navigate(routes.expertRates)}
              >
                <CText type="M14" color={colors.primary}>
                  Set Rates
                </CText>
              </button>
            </div>
          </>
        )}

        {/* Save Button */}
        <div className="mt-8">
          <CButton
            title={loading ? 'Saving...' : 'Save Changes'}
            onPress={handleSave}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalDetails;
